import { Accordion, AccordionDetails, AccordionSummary, Stack } from '@mui/material';
import { PriceDate } from '@retail/my-assortment/types';
import { useCallback, useMemo } from 'react';
import { MarkupAccordionDetails } from './MarkupAccordionDetails';
import { calculateSalesFactorFromDG } from '@retail/utils';
import { ContainedButton, TextButton } from '@shared/components';
import { useAppTFunction } from '@retail/app/i18n';
import { MarkupAccordionSummary } from './MarkupAccordionSummary';
import { EditableMarkupField, useProductCardStore } from '../../store';

interface Props {
  selectedDatedPrices: PriceDate;
  consistsOfCount?: number;
  canEditMarkup: boolean;
  incVat: boolean;
  createMarkupCondition: () => void;
}

export function MarkupConditionAccordion({
  selectedDatedPrices,
  consistsOfCount,
  canEditMarkup,
  incVat,
  createMarkupCondition,
}: Props) {
  const t = useAppTFunction();
  const markupCondition = selectedDatedPrices.markupCondition;
  const costPriceExVat = selectedDatedPrices.priceExVat.costPrice;
  const costPriceIncVat = selectedDatedPrices.priceIncVat.costPrice;
  const markupPrice = incVat
    ? selectedDatedPrices.priceIncVat.markupPrice
    : selectedDatedPrices.priceExVat.markupPrice;
  const markupFactor = markupCondition?.coverageDegree
    ? calculateSalesFactorFromDG(markupCondition.coverageDegree)
    : undefined;

  const {
    newMarkup,
    editingField,
    getSalesPriceEditState,
    setEditingField,
    setNewSalesFactor,
    setNewSalesCoverage,
    setNewSalesPrice,
    closeEditingField,
    resetMarkup,
  } = useProductCardStore();

  const salesPriceEditState = useMemo(
    () => getSalesPriceEditState(!consistsOfCount, !selectedDatedPrices?.priceExVat.costPrice),
    [consistsOfCount, getSalesPriceEditState, selectedDatedPrices?.priceExVat.costPrice]
  );

  const setSalesFactor = useCallback(
    (newFactor: number) => setNewSalesFactor(newFactor, costPriceIncVat, costPriceExVat),
    [costPriceExVat, costPriceIncVat, setNewSalesFactor]
  );
  const setCoverageDegree = useCallback(
    (newSalesCoverage: number) =>
      setNewSalesCoverage(newSalesCoverage, costPriceIncVat, costPriceExVat),
    [costPriceExVat, costPriceIncVat, setNewSalesCoverage]
  );
  const setMarkupPrice = useCallback(
    (newMarkupPrice: number) =>
      setNewSalesPrice(newMarkupPrice, incVat, incVat ? costPriceIncVat : costPriceExVat),
    [costPriceExVat, costPriceIncVat, incVat, setNewSalesPrice]
  );
  return (
    <Accordion>
      <AccordionSummary>
        <MarkupAccordionSummary
          currentMarkupPrice={markupPrice}
          currentCoverageDegree={markupCondition?.coverageDegree}
          isActivePrice={!selectedDatedPrices.priceSuggestionType}
          includeVat={incVat}
          salesPriceEditState={salesPriceEditState}
          newMarkup={newMarkup}
          openEditMode={
            canEditMarkup ? (field: EditableMarkupField) => setEditingField(field) : undefined
          }
          editProps={{
            isEditingCoverage: editingField === 'coverage',
            isEditingPrice: editingField === 'price',
            cancelEditMode: closeEditingField,
            onChangeSalesCoverage: setCoverageDegree,
            onChangeSalesPrice: setMarkupPrice,
          }}
        />
      </AccordionSummary>
      <AccordionDetails>
        <MarkupAccordionDetails
          markupCondition={selectedDatedPrices.markupCondition}
          markupFactor={markupFactor}
          editFactorDisabled={salesPriceEditState === 'noCalculationForPackage'}
          editFactor={editingField === 'factor'}
          newMarkup={newMarkup}
          setSalesFactor={setSalesFactor}
          setEditingFieldToFactor={canEditMarkup ? () => setEditingField('factor') : undefined}
          resetMarkup={resetMarkup}
        />
        {newMarkup && (
          <Stack flexDirection="row" justifyContent="flex-end" alignItems="baseline">
            <Stack flexDirection="row" gap={1} flexWrap="nowrap">
              <TextButton onClick={resetMarkup}>{t('myAssortment.cancel')}</TextButton>
              <ContainedButton
                disabled={!!editingField}
                onClick={createMarkupCondition}
                color="secondary"
                size="small"
              >
                {t('myAssortment.createMarkup')}
              </ContainedButton>
            </Stack>
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
